/**
 * Custom hook for accessing hotel currency information consistently across the app
 */

import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../services/authService';
import { getDefaultCurrency, getCurrencySymbol, getCurrencyDecimals, formatCurrency } from '../utils/currency';

export interface HotelCurrency {
  code: string;
  symbol: string;
  decimals: number;
}

export interface UseHotelCurrencyReturn {
  currency: HotelCurrency;
  formatAmount: (amount: number) => string;
  formatAmountWithCode: (amount: number) => string;
  isLoading: boolean;
}

/**
 * Hook to get hotel currency information from authenticated session
 */
export function useHotelCurrency(): UseHotelCurrencyReturn {
  // Get hotel currency from session validation
  const { data: sessionData, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: () => authService.validateSession(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  // Extract currency from booking details
  const hotelCurrency = sessionData?.data?.booking?.hotel?.currency;
  const currencyCode = hotelCurrency || getDefaultCurrency();
  
  const currency: HotelCurrency = {
    code: currencyCode,
    symbol: getCurrencySymbol(currencyCode),
    decimals: getCurrencyDecimals(currencyCode)
  };

  const formatAmount = (amount: number): string => {
    return formatCurrency(amount, currencyCode);
  };

  const formatAmountWithCode = (amount: number): string => {
    const decimals = getCurrencyDecimals(currencyCode);
    return `${amount.toFixed(decimals)} ${currencyCode.toUpperCase()}`;
  };

  return {
    currency,
    formatAmount,
    formatAmountWithCode,
    isLoading
  };
}

/**
 * Hook to get hotel currency from booking context (for components with booking prop)
 */
export function useHotelCurrencyFromBooking(booking?: any): UseHotelCurrencyReturn {
  const hotelCurrency = booking?.hotel?.currency;
  const currencyCode = hotelCurrency || getDefaultCurrency();
  
  const currency: HotelCurrency = {
    code: currencyCode,
    symbol: getCurrencySymbol(currencyCode),
    decimals: getCurrencyDecimals(currencyCode)
  };

  const formatAmount = (amount: number): string => {
    return formatCurrency(amount, currencyCode);
  };

  const formatAmountWithCode = (amount: number): string => {
    const decimals = getCurrencyDecimals(currencyCode);
    return `${amount.toFixed(decimals)} ${currencyCode.toUpperCase()}`;
  };

  return {
    currency,
    formatAmount,
    formatAmountWithCode,
    isLoading: false
  };
}

/**
 * Hook to get hotel currency from table context (for table ordering)
 */
export function useHotelCurrencyFromTable(tableInfo?: any): UseHotelCurrencyReturn {
  const hotelCurrency = tableInfo?.hotel?.currency;
  const currencyCode = hotelCurrency || getDefaultCurrency();
  
  const currency: HotelCurrency = {
    code: currencyCode,
    symbol: getCurrencySymbol(currencyCode),
    decimals: getCurrencyDecimals(currencyCode)
  };

  const formatAmount = (amount: number): string => {
    return formatCurrency(amount, currencyCode);
  };

  const formatAmountWithCode = (amount: number): string => {
    const decimals = getCurrencyDecimals(currencyCode);
    return `${amount.toFixed(decimals)} ${currencyCode.toUpperCase()}`;
  };

  return {
    currency,
    formatAmount,
    formatAmountWithCode,
    isLoading: false
  };
}